import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Container, Card, Button, Row, Col, Form, Alert, Table, Spinner, InputGroup, Modal } from 'react-bootstrap';
import axios from '../../services/axiosConfig';
import { useAuth } from '../../context/AuthContext';
import { formatDateRange } from '../../utils/dateUtils';

const computeRegistrationStatus = (eventData) => {
  if (!eventData) return 'open';
  try {
    const now = new Date();
    const registrationDeadlineRaw = eventData.registrationDeadline || eventData.registration_deadline;
    const registrationDeadline = registrationDeadlineRaw ? new Date(registrationDeadlineRaw) : null;
    const allowLateRegistration = eventData.allowLateRegistration === true || eventData.allow_late_registration === true;
    const eventDate = new Date(eventData.eventDate || eventData.event_date);

    if (!isNaN(eventDate.getTime()) && now > eventDate) {
      return 'closed';
    }

    if (registrationDeadline && now > registrationDeadline) {
      return allowLateRegistration ? 'late' : 'closed';
    }

    return 'open';
  } catch (error) {
    console.error('Error checking registration status:', error);
    return 'open';
  }
};

const SelectMembers = () => {
  const { id, adminAssociationId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const targetAssociationId = adminAssociationId || (user && user.association_id);
  const isAdminFlow = !!adminAssociationId;

  const handleNavigateBack = () => {
    if (isAdminFlow) {
      navigate(`/admin/events/${id}/participants`);
    } else {
      navigate(`/association/events/${id}`);
    }
  };

  const [event, setEvent] = useState(null);
  const [allMembers, setAllMembers] = useState([]); // Store all members once
  const [members, setMembers] = useState([]);
  const [existingParticipants, setExistingParticipants] = useState([]); // Initialize as empty array
  const [selectedMembers, setSelectedMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [membersLoading, setMembersLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [useFiscalCodes, setUseFiscalCodes] = useState(true); // Use fiscal codes by default
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [totalMembers, setTotalMembers] = useState(0);
  const [dataFetched, setDataFetched] = useState(false);
  
  // State per la gestione della disiscrizione
  const [showUnregisterModal, setShowUnregisterModal] = useState(false);
  const [memberToUnregister, setMemberToUnregister] = useState(null);
  const [unregistering, setUnregistering] = useState(false);

  const limit = 20;

  useEffect(() => {
    const fetchEvent = async () => {
      try {
        const eventResponse = await axios.get(`/api/events/${id}`);
        setEvent(eventResponse.data.event || eventResponse.data);
      } catch (err) {
        console.error('Error fetching event:', err);
        setError('Impossibile caricare i dettagli evento. ' +
          (err.response?.data?.message || 'Verifica la connessione e riprova.'));
      }
    };

    fetchEvent();
  }, [id]);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setPage(1); // Reset page only when debounced term updates
      setDebouncedSearchTerm(searchTerm);
    }, 350);

    return () => clearTimeout(timeoutId);
  }, [searchTerm]);

  useEffect(() => {
    const fetchMembersAndParticipants = async () => {
      if (dataFetched) return; // Only fetch once

      try {
        setError(null);

        if (!targetAssociationId) {
          setLoading(false);
          return;
        }

        const membersResponse = await axios.get(`/api/associations/${targetAssociationId}/members`, {
          params: {
            limit: 10000 // Fetch essentially all members for client-side filtering
          }
        });

        const fetchedMembers = membersResponse.data.members || [];
        setAllMembers(fetchedMembers);
        
        try {
          const participantsResponse = await axios.get(
            `/api/associations/${targetAssociationId}/events/${id}/participants`
          );

          if (participantsResponse.data && participantsResponse.data.success) {
            setExistingParticipants(participantsResponse.data.participants || []);
          } else {
            setExistingParticipants(participantsResponse.data || []);
          }
        } catch (participantsError) {
          console.error('Error fetching existing participants:', participantsError);
          setExistingParticipants([]);

          if (!(participantsError.response && participantsError.response.status === 404)) {
            setError('Impossibile verificare i partecipanti già registrati. Alcuni membri potrebbero apparire come disponibili anche se già registrati.');
          }
        }
        
        setDataFetched(true);
      } catch (err) {
        console.error('Error fetching members:', err);

        if (err.response && err.response.status === 404) {
          setAllMembers([]);
          setError('La tua associazione non ha membri registrati nel sistema. Aggiungi membri prima di procedere.');
        } else {
          setError('Errore nel recupero dei membri dell\'associazione. ' +
            (err.response?.data?.message || 'Verifica la connessione e riprova.'));
        }
      } finally {
        setLoading(false);
      }
    };

    fetchMembersAndParticipants();
  }, [id, targetAssociationId, dataFetched]);

  const registrationStatus = computeRegistrationStatus(event);
  const isRegistrationOpen = registrationStatus !== 'closed';

  // Client-side filtering and pagination
  useEffect(() => {
    if (!dataFetched) return;
    setMembersLoading(true);

    const term = debouncedSearchTerm.toLowerCase();
    
    // 1. Filter by search term
    const filtered = allMembers.filter(member => {
      if (!term) return true;
      const fullName = `${member.first_name || ''} ${member.last_name || ''}`.toLowerCase();
      const name = (member.name || '').toLowerCase();
      const email = (member.email || '').toLowerCase();
      const fiscalCode = (member.fiscal_code || '').toLowerCase();
      
      return fullName.includes(term) || name.includes(term) || email.includes(term) || fiscalCode.includes(term);
    });

    // 2. Paginate
    setTotalMembers(filtered.length);
    setTotalPages(Math.ceil(filtered.length / limit) || 1);
    
    const startIndex = (page - 1) * limit;
    const paginatedMembers = filtered.slice(startIndex, startIndex + limit);
    
    setMembers(paginatedMembers);
    setMembersLoading(false);
  }, [debouncedSearchTerm, page, allMembers, dataFetched, limit]);

  const handleSearchInputChange = (e) => {
    setSearchTerm(e.target.value);
  };

  const clearSearch = () => {
    setSearchTerm('');
    setPage(1);
  };

  const handlePageChange = (newPage) => {
    setPage(newPage);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleCheckboxChange = (memberId) => {
    if (!isRegistrationOpen) return;
    // Se il membro è già registrato, apri il modale di disiscrizione
    if (isAlreadyRegistered(memberId)) {
      const member = allMembers.find(m => m.id === memberId || m._id === memberId);
      if (member) {
        setMemberToUnregister(member);
        setShowUnregisterModal(true);
      }
      return;
    }

    setSelectedMembers(prev => {
      if (prev.includes(memberId)) {
        return prev.filter(id => id !== memberId);
      } else {
        return [...prev, memberId];
      }
    });
  };

  const getParticipantInfo = (memberId) => {
    if (!existingParticipants || !Array.isArray(existingParticipants) || existingParticipants.length === 0) return null;
    const member = allMembers.find(m => m.id === memberId || m._id === memberId);
    if (!member) return null;

    return existingParticipants.find(p => {
      if (member.id && (String(p.member_id) === String(member.id) || String(p.id) === String(member.id))) return true;
      if (member.fiscal_code && p.fiscal_code && p.fiscal_code.toLowerCase() === member.fiscal_code.toLowerCase()) return true;
      if (participantMatchFallback(p, member)) return true;
      return false;
    });
  };

  const participantMatchFallback = (participant, member) => {
    const emailMatch = participant.email && member.email && participant.email.toLowerCase() === member.email.toLowerCase();
    const nameMatch = (participant.first_name && participant.last_name && member.first_name && member.last_name &&
             `${participant.first_name} ${participant.last_name}`.toLowerCase() === `${member.first_name} ${member.last_name}`.toLowerCase()) ||
           (participant.name && member.name && participant.name.toLowerCase() === member.name.toLowerCase());

    // Require both email and name to match to avoid false positives for family members sharing an email
    if (participant.email && member.email) {
      return emailMatch && nameMatch;
    }
    return nameMatch;
  };

  const confirmUnregister = async () => {
    if (!memberToUnregister) return;

    try {
      setUnregistering(true);
      setError(null);
      setSuccess(null);

      const memberId = memberToUnregister.id || memberToUnregister._id;
      const participantInfo = getParticipantInfo(memberId);
      
      if (!participantInfo || (!participantInfo.id && !participantInfo._id)) {
        throw new Error("Impossibile trovare l'ID della registrazione per questo membro.");
      }

      const participantId = participantInfo._id || participantInfo.id;
      
      // Make the API call to unregister
      await axios.delete(`/api/events/${id}/participants/${participantId}`);
      
      setSuccess(`Disiscrizione completata per ${memberToUnregister.name || memberToUnregister.first_name || 'il membro'}.`);
      
      // Remove from existingParticipants and selectedMembers immediately
      setExistingParticipants(prev => prev.filter(p => p.id !== participantId && p._id !== participantId));
      setSelectedMembers(prev => prev.filter(id => id !== memberId));
      
      setShowUnregisterModal(false);
      setMemberToUnregister(null);
    } catch (err) {
      console.error('Error unregistering member:', err);
      setError('Errore durante la disiscrizione: ' + (err.response?.data?.message || err.message || 'errore sconosciuto'));
      setShowUnregisterModal(false);
    } finally {
      setUnregistering(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!isRegistrationOpen) {
      setError('Le iscrizioni sono chiuse. Non e possibile modificare i partecipanti.');
      return;
    }

    if (selectedMembers.length === 0) {
      setError('Seleziona almeno un membro.');
      return;
    }

    try {
      setSubmitting(true);
      setError(null);

      // Get fiscal codes for the selected members
      const selectedFiscalCodes = allMembers
        .filter(member => selectedMembers.includes(member.id || member._id))
        .map(member => member.fiscal_code)
        .filter(fiscalCode => fiscalCode); // Filter out any undefined fiscal codes

      // If some members don't have fiscal codes, fall back to member_ids
      const payload = {
        association_id: targetAssociationId,
        fiscal_codes: selectedFiscalCodes
      };

      // If any member doesn't have a fiscal code, include member_ids as a fallback
      if (selectedFiscalCodes.length < selectedMembers.length) {
        payload.member_ids = selectedMembers;
      }

      // Submit members to the API
      const response = await axios.post(`/api/events/${id}/register-members`, payload);

      if (response.data.success) {
        setSuccess(`${selectedMembers.length} membri registrati con successo all'evento!`);

        // Reset selection
        setSelectedMembers([]);

        // Redirect back to event details after a delay
        setTimeout(() => {
          handleNavigateBack();
        }, 2000);
      } else {
        setError('Errore durante la registrazione: ' + (response.data.message || 'errore sconosciuto'));
      }

    } catch (err) {
      console.error('Error registering members:', err);
      setError('Errore durante la registrazione dei membri: ' + (err.response?.data?.message || err.message || 'errore sconosciuto'));
    } finally {
      setSubmitting(false);
    }
  };

  // Funzione per verificare se un membro è già registrato all'evento
  const isAlreadyRegistered = (memberId) => {
    // Make sure we have valid arrays
    if (!existingParticipants || !Array.isArray(existingParticipants) || existingParticipants.length === 0) return false;
    if (!allMembers || !Array.isArray(allMembers) || allMembers.length === 0) return false;

    // Cerca il membro tra i partecipanti esistenti confrontando nome ed email
    const member = allMembers.find(m => m.id === memberId || m._id === memberId);
    if (!member) return false;

    // Safety check to make sure .some is available
    if (!Array.isArray(existingParticipants)) {
      console.error('existingParticipants is not an array:', existingParticipants);
      return false;
    }

    try {
      // First try to match by member_id (most reliable), ignoring type differences
      if (member.id) {
        const matchById = existingParticipants.some(p => String(p.member_id) === String(member.id) || String(p.id) === String(member.id));
        if (matchById) return true;
      }

      // Match by fiscal code (very reliable)
      if (member.fiscal_code) {
        const matchByFc = existingParticipants.some(p => 
          p.fiscal_code && typeof p.fiscal_code === 'string' && typeof member.fiscal_code === 'string' && 
          p.fiscal_code.toLowerCase() === member.fiscal_code.toLowerCase()
        );
        if (matchByFc) return true;
      }

      // Then try matching by email or name
      return existingParticipants.some(participant => participantMatchFallback(participant, member));
    } catch (error) {
      console.error('Error in isAlreadyRegistered:', error);
      return false;
    }
  };

  if (loading) {
    return (
      <div className="text-center mt-5">
        <div className="spinner-border text-success" role="status">
          <span className="visually-hidden">Caricamento...</span>
        </div>
        <p className="mt-3">Caricamento dati...</p>
      </div>
    );
  }

  return (
    <Container className="mt-4">
      <Button
        variant="outline-secondary"
        onClick={() => handleNavigateBack()}
        className="mb-4"
      >
        ← Torna ai dettagli evento
      </Button>
      <Card className="mb-4">
        <Card.Header style={{ backgroundColor: "#006e39", color: "white" }}>
          <h4>Seleziona i membri per la registrazione all'evento</h4>
        </Card.Header>
        <Card.Body>
          {!targetAssociationId ? (
            <Alert variant="warning">
              Devi essere autenticato come associazione per registrare i membri.
            </Alert>
          ) : loading ? (
            <div className="text-center my-5">
              <Spinner animation="border" role="status" variant="success">
                <span className="visually-hidden">Caricamento...</span>
              </Spinner>
              <p className="mt-3">Caricamento dei membri dell'associazione...</p>
            </div>) : allMembers.length === 0 && !membersLoading ? (
              <Alert variant="info">
                <div className="text-center">
                  <div className="mb-4">
                    <InputGroup className="mb-3" size="lg">
                      <InputGroup.Text style={{ backgroundColor: '#f8f9fa', borderColor: '#006e39' }}>
                        <i className="bi bi-search" style={{ color: '#006e39' }}></i>
                      </InputGroup.Text>
                      <Form.Control
                        placeholder="Cerca per nome, cognome, email o codice fiscale..."
                        value={searchTerm}
                        onChange={handleSearchInputChange}
                        style={{ borderColor: '#006e39' }}
                        disabled={true}
                      />
                    </InputGroup>
                    <i className="bi bi-exclamation-triangle" style={{ fontSize: '2rem', color: '#ffc107' }}></i>
                  </div>
                  <h5>Nessun membro trovato per la tua associazione.</h5>
                  <p>È necessario aggiungere membri all'associazione prima di poterli registrare agli eventi.</p>
                  <div className="mt-3">
                    <Button
                      onClick={() => navigate('/association/members')}
                      variant="success"
                      style={{ backgroundColor: "#006e39", borderColor: "#006e39" }}
                    >                      <i className="bi bi-search me-2"></i>
                      Cerca membri dell'associazione
                    </Button>
                  </div>
                </div>
              </Alert>
            ) : (
            <Form onSubmit={handleSubmit}>
              {error && <Alert variant="danger">{error}</Alert>}
              {success && <Alert variant="success">{success}</Alert>}
              {!isRegistrationOpen && (
                <Alert variant="warning" className="mb-3">
                  Le iscrizioni sono chiuse. Puoi consultare i membri gia registrati ma non modificare le iscrizioni.
                </Alert>
              )}
              <Row className="mb-3">
                <Col>
                  <h5>Evento: {event?.title}</h5>
                  <p>Data: {formatDateRange(event?.eventDate || event?.event_date, event?.eventEndDate || event?.event_end_date || event?.eventDate || event?.event_date)}</p>
                </Col>
                <Col xs="auto">
                  <Form.Check
                    type="switch"
                    id="use-fiscal-codes"
                    label="Usa codici fiscali"
                    checked={useFiscalCodes}
                    onChange={(e) => setUseFiscalCodes(e.target.checked)}
                  />
                </Col>
              </Row>

              <div className="mb-3">
                <InputGroup>
                  <InputGroup.Text style={{ backgroundColor: '#f8f9fa', borderColor: '#006e39' }}>
                    <i className="bi bi-search" style={{ color: '#006e39' }}></i>
                  </InputGroup.Text>
                  <Form.Control
                    placeholder="Cerca per nome, cognome, email o codice fiscale..."
                    value={searchTerm}
                    onChange={handleSearchInputChange}
                    aria-label="Ricerca membri"
                    style={{ borderColor: '#006e39' }}
                  />
                  {searchTerm && (
                    <Button
                      variant="outline-danger"
                      onClick={clearSearch}
                      title="Cancella ricerca"
                    >
                      <i className="bi bi-x-lg"></i>
                    </Button>
                  )}
                </InputGroup>
                {searchTerm && (
                  <small className="text-muted">
                    Risultati filtrati per: <strong>{searchTerm}</strong>
                  </small>
                )}
                {membersLoading && (
                  <div className="mt-2 text-muted small">
                    <Spinner animation="border" size="sm" className="me-2" />
                    Aggiornamento risultati...
                  </div>
                )}
              </div>

              <div className="mb-4">
                <div className="alert alert-light border">
                  <div className="d-flex flex-wrap gap-2 justify-content-between">
                    <div>
                      <strong>Membri disponibili totali:</strong> {
                        Array.isArray(allMembers) ?
                          allMembers.filter(m => !isAlreadyRegistered(m.id || m._id)).length :
                          0
                      }
                    </div>
                    <div>
                      <strong>Già registrati totali:</strong> {
                        Array.isArray(existingParticipants) ? existingParticipants.length : 0
                      }
                    </div>
                    <div>
                      <strong>Selezionati (da registrare):</strong> {selectedMembers.length}
                    </div>
                    <div>
                      <strong>Totale dei risultati di ricerca:</strong> {totalMembers}
                    </div>
                  </div>
                </div>
              </div>
              <div className="table-responsive">
                <Table striped bordered hover>
                  <thead>
                    <tr>
                      <th style={{ width: '50px' }}>
                        <Form.Check
                          type="checkbox"
                          id="select-all"
                          disabled={!isRegistrationOpen}
                          onChange={(e) => {
                            if (e.target.checked) {
                                // Seleziona tutti quelli non già registrati nella pagina corrente 
                                // e unisci con i già selezionati
                                const currentNonRegisteredIds = members
                                  .filter(m => !isAlreadyRegistered(m.id || m._id))
                                  .map(m => m.id || m._id);
                                
                                setSelectedMembers(prev => {
                                  const newSet = new Set([...prev, ...currentNonRegisteredIds]);
                                  return Array.from(newSet);
                                });
                              } else {
                                // Deseleziona solo i membri della pagina corrente
                                const currentPageIds = members.map(m => m.id || m._id);
                                setSelectedMembers(prev => prev.filter(id => !currentPageIds.includes(id)));
                            }
                          }}
                          checked={
                            members.length > 0 &&
                            members.filter(m => !isAlreadyRegistered(m.id || m._id)).length > 0 &&
                              members.filter(m => !isAlreadyRegistered(m.id || m._id)).every(m => selectedMembers.includes(m.id || m._id))
                          }
                        />
                      </th>
                      <th>Nome</th>
                      <th>Email</th>
                      <th>Sport</th>
                      {useFiscalCodes && <th>Codice Fiscale</th>}
                      <th>Stato</th>
                    </tr>
                  </thead>
                  <tbody>
                    {members.length === 0 && !membersLoading && (
                      <tr>
                        <td colSpan={useFiscalCodes ? 6 : 5} className="text-center text-muted py-4">
                          Nessun risultato per la ricerca corrente.
                        </td>
                      </tr>
                    )}
                    {members.map(member => {
                      const memberId = member.id || member._id;
                      const alreadyRegistered = isAlreadyRegistered(memberId);

                      return (
                        <tr
                          key={memberId}
                          className={alreadyRegistered ? "table-secondary" : ""}
                        >
                          <td className="text-center">
                            <Form.Check
                              type="checkbox"
                              id={`member-${memberId}`}
                              checked={alreadyRegistered || selectedMembers.includes(memberId)}
                              onChange={() => handleCheckboxChange(memberId)}
                              disabled={!isRegistrationOpen}
                            />
                          </td>
                          <td>{member.name || member.fullName || `${member.first_name} ${member.last_name}`}</td>
                          <td>{member.email}</td>
                          <td>{member.sport || 'N/A'}</td>
                          {useFiscalCodes && <td>{member.fiscal_code || 'N/A'}</td>}
                          {alreadyRegistered && (
                            <td>
                              <span className="badge bg-success">Già registrato</span>
                            </td>
                          )}
                          {!alreadyRegistered && <td></td>}
                        </tr>
                      );
                    })}
                  </tbody>
                </Table>
              </div>

              {totalPages > 1 && (
                <div className="d-flex justify-content-center align-items-center mt-3">
                  <Button
                    variant="outline-success"
                    onClick={() => handlePageChange(page - 1)}
                    disabled={page <= 1 || membersLoading}
                    className="me-2"
                  >
                    <i className="bi bi-chevron-left"></i> Precedente
                  </Button>
                  <span className="mx-3">
                    Pagina {page} di {totalPages}
                  </span>
                  <Button
                    variant="outline-success"
                    onClick={() => handlePageChange(page + 1)}
                    disabled={page >= totalPages || membersLoading}
                    className="ms-2"
                  >
                    Successivo <i className="bi bi-chevron-right"></i>
                  </Button>
                </div>
              )}

              {submitting && (
                <div className="text-center my-3">
                  <Spinner animation="border" variant="success" role="status">
                    <span className="visually-hidden">Registrazione in corso...</span>
                  </Spinner>
                  <p className="mt-2">Registrazione dei membri in corso...</p>
                </div>
              )}              <div className="d-flex flex-wrap justify-content-end gap-2 mt-4">
                <Button
                  variant="secondary"
                  onClick={() => handleNavigateBack()}
                  disabled={submitting}
                >
                  Annulla
                </Button>
                <Button
                  variant="primary"
                  type="submit"
                  disabled={submitting || selectedMembers.length === 0 || !isRegistrationOpen}
                  style={{ backgroundColor: "#006e39", borderColor: "#006e39" }}
                >
                  {submitting ? 'Registrazione in corso...' : 'Registra membri selezionati'}
                </Button>
              </div>
            </Form>
          )}
        </Card.Body>
      </Card>

      {/* Modal Disiscrizione */}
      <Modal show={showUnregisterModal} onHide={() => !unregistering && setShowUnregisterModal(false)} backdrop="static" keyboard={false}>
        <Modal.Header closeButton={!unregistering}>
          <Modal.Title>Conferma disiscrizione</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="d-flex align-items-center mb-3 text-warning">
            <i className="bi bi-exclamation-triangle-fill fs-3 me-3"></i>
            <strong>Attenzione</strong>
          </div>
          <p>
            Stai disiscrivendo <strong>{memberToUnregister?.name || memberToUnregister?.fullName || `${memberToUnregister?.first_name} ${memberToUnregister?.last_name}`}</strong> dall'evento <strong>{event?.title}</strong>.
          </p>
          <p>Sei sicuro di voler procedere?</p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowUnregisterModal(false)} disabled={unregistering}>
            Annulla
          </Button>
          <Button variant="danger" onClick={confirmUnregister} disabled={unregistering}>
            {unregistering ? (
              <>
                <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" className="me-2" />
                Disiscrizione in corso...
              </>
            ) : "Conferma disiscrizione"}
          </Button>
        </Modal.Footer>
      </Modal>

    </Container>
  );
};

export default SelectMembers;


